import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui';
import { InfoIcon } from '../../../components/ui/Icons';
import api from '../../../services/api';

export interface MonthlyBillingSummary {
  year: number;
  month: number;
  monthLabel: string;
  currency: string;
  totalCostUsd: number;
  totalTokens: number;
  requestCount: number;
  byType: Array<{
    type: string;
    label: string;
    description?: string;
    costUsd: number;
    tokens: number;
    requests: number;
  }>;
  byModel: Array<{
    model: string;
    costUsd: number;
    tokens: number;
    requests: number;
  }>;
  byDay: Array<{
    date: string;
    day: number;
    costUsd: number;
    requests: number;
  }>;
  recent: Array<{
    id: number;
    type: string;
    label: string;
    description?: string;
    model: string;
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
    costUsd: number;
    createdAt: string;
    userName?: string | null;
    metadata?: Record<string, unknown> | null;
  }>;
  pricingNote: string;
}

const FEATURE_LABELS: Record<string, string> = {
  document_scan: 'Document AI scan',
};

function formatUsd(value: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  }).format(value);
}

function formatUsdShort(value: number) {
  if (value === 0) return '$0.00';
  if (value < 0.01) return '< $0.01';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(value);
}

function metaString(meta: Record<string, unknown> | null | undefined, key: string): string | null {
  const value = meta?.[key];
  if (value == null || value === '') return null;
  return String(value);
}

function ActivityInfoTip({
  row,
}: {
  row: MonthlyBillingSummary['recent'][number];
}) {
  const { colors } = useTheme();
  const featureKey = metaString(row.metadata, 'feature');
  const featureLabel = featureKey ? FEATURE_LABELS[featureKey] || featureKey.replace(/_/g, ' ') : null;
  const documentId = metaString(row.metadata, 'documentId');
  const mimeType = metaString(row.metadata, 'mimeType');
  const approvalId = metaString(row.metadata, 'approvalId');

  const details: Array<{ label: string; value: React.ReactNode }> = [];
  if (featureLabel) details.push({ label: 'Feature', value: featureLabel });
  if (row.userName) details.push({ label: 'Triggered by', value: row.userName });
  if (documentId) {
    details.push({
      label: 'Document',
      value: (
        <Link
          to={`/documents/${documentId}`}
          className="font-medium text-sky-600 hover:underline dark:text-sky-400"
          onClick={(e) => e.stopPropagation()}
        >
          #{documentId}
        </Link>
      ),
    });
  }
  if (approvalId) {
    details.push({
      label: 'Approval',
      value: (
        <Link
          to={`/approvals/${approvalId}`}
          className="font-medium text-sky-600 hover:underline dark:text-sky-400"
          onClick={(e) => e.stopPropagation()}
        >
          #{approvalId}
        </Link>
      ),
    });
  }
  if (mimeType) details.push({ label: 'File type', value: mimeType });
  details.push({ label: 'Model', value: row.model });

  return (
    <span className="group/info relative inline-flex shrink-0">
      <button
        type="button"
        className={cn(
          'inline-flex h-5 w-5 items-center justify-center rounded-full transition',
          'text-slate-400 hover:bg-slate-100 hover:text-sky-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400',
          'dark:hover:bg-slate-800 dark:hover:text-sky-300',
        )}
        aria-label={`About ${row.label}`}
      >
        <InfoIcon className="h-3.5 w-3.5" />
      </button>
      <span
        role="tooltip"
        className={cn(
          'pointer-events-none absolute left-1/2 top-full z-30 mt-2 w-64 -translate-x-1/2 rounded-xl border px-3 py-2.5 text-left shadow-lg',
          'opacity-0 transition duration-150 group-hover/info:pointer-events-auto group-hover/info:opacity-100 group-focus-within/info:pointer-events-auto group-focus-within/info:opacity-100',
          colors.border,
          colors.surface,
        )}
      >
        <span className={cn('block text-xs font-semibold', colors.text)}>{row.label}</span>
        <span className={cn('mt-1 block text-[11px] leading-relaxed', colors.textMuted)}>
          {row.description || 'Recorded AI API usage for this request.'}
        </span>
        {details.length > 0 && (
          <span className={cn('mt-2 block space-y-1 border-t pt-2', colors.border)}>
            {details.map((item) => (
              <span key={item.label} className="flex items-start justify-between gap-2 text-[11px]">
                <span className={cn('shrink-0', colors.textMuted)}>{item.label}</span>
                <span className={cn('min-w-0 text-right font-medium', colors.text)}>{item.value}</span>
              </span>
            ))}
          </span>
        )}
      </span>
    </span>
  );
}

function ShareBar({
  value,
  max,
  className,
}: {
  value: number;
  max: number;
  className?: string;
}) {
  const pct = max > 0 ? Math.max(2, (value / max) * 100) : 0;
  return (
    <div className={cn('h-1.5 w-full overflow-hidden rounded-full bg-slate-200/80 dark:bg-slate-700/80', className)}>
      <div
        className="h-full rounded-full bg-sky-500 dark:bg-sky-400 transition-[width] duration-300"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

export const AiBillingPage: React.FC = () => {
  const { colors } = useTheme();
  const now = useMemo(() => new Date(), []);
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [summary, setSummary] = useState<MonthlyBillingSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hoveredDay, setHoveredDay] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get<MonthlyBillingSummary>('/billing/monthly', {
        params: { year, month },
        skipGlobalErrorToast: true,
      })
      .then((res) => {
        if (!cancelled) setSummary(res.data);
      })
      .catch(() => {
        if (!cancelled) {
          setSummary(null);
          setError('Could not load AI billing data.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [year, month]);

  const maxDayCost = Math.max(...(summary?.byDay.map((d) => d.costUsd) ?? [0]), 0.0001);
  const maxTypeCost = Math.max(...(summary?.byType.map((d) => d.costUsd) ?? [0]), 0.0001);
  const maxModelCost = Math.max(...(summary?.byModel.map((d) => d.costUsd) ?? [0]), 0.0001);
  const hovered = summary?.byDay.find((d) => d.date === hoveredDay);

  const shiftMonth = (delta: number) => {
    const date = new Date(year, month - 1 + delta, 1);
    setYear(date.getFullYear());
    setMonth(date.getMonth() + 1);
  };

  return (
    <div className={cn('flex h-full min-h-0 w-full flex-col gap-4 overflow-auto', colors.background)}>
      <header className={cn('flex flex-wrap items-center justify-between gap-3 rounded-2xl border px-4 py-3 shadow-sm', colors.surface, colors.border)}>
        <div className="min-w-0">
          <h1 className={cn('text-base font-semibold', colors.text)}>AI usage & cost</h1>
          <p className={cn('text-xs', colors.textMuted)}>
            Company-wide estimated OpenAI spend for document scans and related AI calls.
          </p>
        </div>
        <div className={cn('inline-flex items-center gap-1 rounded-xl border px-1.5 py-1', colors.border, colors.background)}>
          <Button type="button" variant="ghost" size="sm" onClick={() => shiftMonth(-1)} aria-label="Previous month">
            ‹
          </Button>
          <span className={cn('min-w-[11rem] select-none text-center text-sm font-semibold capitalize', colors.text)}>
            {summary?.monthLabel ?? `${month}/${year}`}
          </span>
          <Button type="button" variant="ghost" size="sm" onClick={() => shiftMonth(1)} aria-label="Next month">
            ›
          </Button>
        </div>
      </header>

      {loading && (
        <div className={cn('flex flex-1 items-center justify-center rounded-2xl border px-5 py-16 text-sm', colors.border, colors.surface, colors.textMuted)}>
          Loading billing summary…
        </div>
      )}

      {!loading && error && (
        <div className={cn('rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300')}>
          {error}
        </div>
      )}

      {!loading && summary && (
        <div className="flex min-h-0 flex-1 flex-col gap-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className={cn('relative overflow-hidden rounded-2xl border px-5 py-4 shadow-sm', colors.border, colors.surface)}>
              <div className="pointer-events-none absolute inset-y-0 left-0 w-1 bg-sky-500" />
              <span className={cn('block text-[11px] font-semibold uppercase tracking-wide', colors.textMuted)}>
                Month total
              </span>
              <strong className={cn('mt-1.5 block text-3xl font-semibold tracking-tight', colors.primaryFg)}>
                {formatUsd(summary.totalCostUsd)}
              </strong>
              <p className={cn('mt-1 text-xs', colors.textMuted)}>Estimated USD</p>
            </div>
            <div className={cn('rounded-2xl border px-5 py-4 shadow-sm', colors.border, colors.surface)}>
              <span className={cn('block text-[11px] font-semibold uppercase tracking-wide', colors.textMuted)}>
                AI requests
              </span>
              <strong className={cn('mt-1.5 block text-3xl font-semibold tracking-tight', colors.text)}>
                {summary.requestCount.toLocaleString()}
              </strong>
              <p className={cn('mt-1 text-xs', colors.textMuted)}>Calls this month</p>
            </div>
            <div className={cn('rounded-2xl border px-5 py-4 shadow-sm', colors.border, colors.surface)}>
              <span className={cn('block text-[11px] font-semibold uppercase tracking-wide', colors.textMuted)}>
                Tokens
              </span>
              <strong className={cn('mt-1.5 block text-3xl font-semibold tracking-tight', colors.text)}>
                {summary.totalTokens.toLocaleString()}
              </strong>
              <p className={cn('mt-1 text-xs', colors.textMuted)}>Input + output</p>
            </div>
          </div>

          <p className={cn('px-0.5 text-xs', colors.textMuted)}>{summary.pricingNote}</p>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
            <section className={cn('flex flex-col rounded-2xl border shadow-sm xl:col-span-3', colors.border, colors.surface)}>
              <div className={cn('border-b px-5 py-3', colors.border)}>
                <h2 className={cn('text-sm font-semibold', colors.text)}>By feature</h2>
              </div>
              <div className="flex-1 px-4 py-3">
                {summary.byType.length === 0 ? (
                  <p className={cn('px-1 py-6 text-center text-sm', colors.textMuted)}>No AI usage this month.</p>
                ) : (
                  <ul className="space-y-3">
                    {summary.byType.map((item) => (
                      <li key={item.type} className="px-1">
                        <div className="mb-1.5 flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <p className={cn('text-sm font-medium', colors.text)}>{item.label}</p>
                              {item.description && (
                                <span className="group/type relative inline-flex">
                                  <span
                                    className={cn(
                                      'inline-flex h-4 w-4 cursor-help items-center justify-center rounded-full text-slate-400',
                                      'hover:text-sky-600 dark:hover:text-sky-300',
                                    )}
                                    title={item.description}
                                    aria-label={item.description}
                                  >
                                    <InfoIcon className="h-3.5 w-3.5" />
                                  </span>
                                  <span
                                    role="tooltip"
                                    className={cn(
                                      'pointer-events-none absolute left-0 top-full z-20 mt-1.5 w-52 rounded-lg border px-2.5 py-2 text-[11px] leading-relaxed shadow-lg',
                                      'opacity-0 transition group-hover/type:opacity-100',
                                      colors.border,
                                      colors.surface,
                                      colors.textMuted,
                                    )}
                                  >
                                    {item.description}
                                  </span>
                                </span>
                              )}
                            </div>
                            <p className={cn('text-xs', colors.textMuted)}>
                              {item.requests} req · {item.tokens.toLocaleString()} tok
                            </p>
                          </div>
                          <span className={cn('shrink-0 text-sm font-semibold tabular-nums', colors.text)}>
                            {formatUsdShort(item.costUsd)}
                          </span>
                        </div>
                        <ShareBar value={item.costUsd} max={maxTypeCost} />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>

            <section className={cn('flex flex-col rounded-2xl border shadow-sm xl:col-span-3', colors.border, colors.surface)}>
              <div className={cn('border-b px-5 py-3', colors.border)}>
                <h2 className={cn('text-sm font-semibold', colors.text)}>By model</h2>
              </div>
              <div className="flex-1 px-4 py-3">
                {summary.byModel.length === 0 ? (
                  <p className={cn('px-1 py-6 text-center text-sm', colors.textMuted)}>—</p>
                ) : (
                  <ul className="space-y-3">
                    {summary.byModel.map((item) => (
                      <li key={item.model} className="px-1">
                        <div className="mb-1.5 flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className={cn('truncate text-sm font-medium', colors.text)}>{item.model}</p>
                            <p className={cn('text-xs', colors.textMuted)}>{item.requests} requests</p>
                          </div>
                          <span className={cn('shrink-0 text-sm font-semibold tabular-nums', colors.text)}>
                            {formatUsdShort(item.costUsd)}
                          </span>
                        </div>
                        <ShareBar value={item.costUsd} max={maxModelCost} />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>

            <section className={cn('flex min-h-[18rem] flex-col rounded-2xl border shadow-sm xl:col-span-6', colors.border, colors.surface)}>
              <div className={cn('flex flex-wrap items-center justify-between gap-2 border-b px-5 py-3', colors.border)}>
                <h2 className={cn('text-sm font-semibold', colors.text)}>Daily usage</h2>
                <span className={cn('text-xs tabular-nums', colors.textMuted)}>
                  {hovered
                    ? `${hovered.date} · ${formatUsdShort(hovered.costUsd)} · ${hovered.requests} req`
                    : 'Hover a day for details'}
                </span>
              </div>
              <div className="flex flex-1 items-end gap-1 px-4 pb-3 pt-4">
                {summary.byDay.map((day) => {
                  const active = hoveredDay === day.date;
                  const hasCost = day.costUsd > 0;
                  return (
                    <div
                      key={day.date}
                      className="group flex h-full min-w-0 flex-1 flex-col items-center justify-end"
                      onMouseEnter={() => setHoveredDay(day.date)}
                      onMouseLeave={() => setHoveredDay(null)}
                    >
                      <div
                        className={cn(
                          'w-full max-w-[1.75rem] rounded-t-md transition-all duration-200',
                          hasCost
                            ? active
                              ? 'bg-sky-600 dark:bg-sky-300'
                              : 'bg-sky-500/90 dark:bg-sky-400/90 group-hover:bg-sky-600 dark:group-hover:bg-sky-300'
                            : 'bg-slate-200 dark:bg-slate-700',
                        )}
                        style={{
                          height: hasCost
                            ? `${Math.max(6, (day.costUsd / maxDayCost) * 100)}%`
                            : '4px',
                        }}
                      />
                      <span
                        className={cn(
                          'mt-1.5 text-[10px] tabular-nums',
                          active ? colors.text : colors.textMuted,
                          day.day % 5 === 1 || day.day === summary.byDay.length ? 'opacity-100' : 'opacity-0 sm:opacity-100',
                        )}
                      >
                        {day.day}
                      </span>
                    </div>
                  );
                })}
              </div>
            </section>
          </div>

          <section className={cn('flex min-h-0 flex-1 flex-col rounded-2xl border shadow-sm', colors.border, colors.surface)}>
            <div className={cn('flex items-center justify-between border-b px-5 py-3', colors.border)}>
              <h2 className={cn('text-sm font-semibold', colors.text)}>Recent activity</h2>
              <span className={cn('text-xs', colors.textMuted)}>Last {summary.recent.length || 0} of month</span>
            </div>
            {summary.recent.length === 0 ? (
              <p className={cn('px-5 py-10 text-center text-sm', colors.textMuted)}>
                No usage recorded yet. Run a document AI scan to see activity here.
              </p>
            ) : (
              <div className="min-h-0 flex-1 overflow-auto">
                <table className="w-full border-collapse text-sm">
                  <thead className={cn('sticky top-0 z-10', colors.surface)}>
                    <tr className={cn('border-b text-left text-[11px] font-semibold uppercase tracking-wide', colors.border, colors.textMuted)}>
                      <th className="px-5 py-2.5">Date</th>
                      <th className="px-4 py-2.5">Type</th>
                      <th className="px-4 py-2.5">Model</th>
                      <th className="px-4 py-2.5 text-right">Input</th>
                      <th className="px-4 py-2.5 text-right">Output</th>
                      <th className="px-4 py-2.5 text-right">Tokens</th>
                      <th className="px-5 py-2.5 text-right">Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.recent.map((row) => (
                      <tr
                        key={row.id}
                        className={cn('border-b transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/40', colors.border)}
                      >
                        <td className={cn('whitespace-nowrap px-5 py-2.5', colors.text)}>
                          {new Date(row.createdAt).toLocaleString()}
                        </td>
                        <td className="px-4 py-2.5">
                          <span className="inline-flex items-center gap-1.5">
                            <span className={cn('inline-flex rounded-md px-2 py-0.5 text-xs font-medium', colors.surfaceHover, colors.text)}>
                              {row.label}
                            </span>
                            <ActivityInfoTip row={row} />
                          </span>
                        </td>
                        <td className={cn('px-4 py-2.5 font-mono text-xs', colors.text)}>{row.model}</td>
                        <td className={cn('px-4 py-2.5 text-right tabular-nums', colors.textMuted)}>
                          {row.inputTokens.toLocaleString()}
                        </td>
                        <td className={cn('px-4 py-2.5 text-right tabular-nums', colors.textMuted)}>
                          {row.outputTokens.toLocaleString()}
                        </td>
                        <td className={cn('px-4 py-2.5 text-right tabular-nums', colors.text)}>
                          {row.totalTokens.toLocaleString()}
                        </td>
                        <td className={cn('px-5 py-2.5 text-right font-semibold tabular-nums', colors.text)}>
                          {formatUsdShort(row.costUsd)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
};
